import React from 'react';
import { useDropzone } from 'react-dropzone';

interface PreviewSectionProps {
  file: File | null;
  onFileSelected: (file: File) => void;
}

export function PreviewSection({ file, onFileSelected }: PreviewSectionProps) {
  const previewUrl = file ? URL.createObjectURL(file) : null;

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      onFileSelected(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  React.useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!file) {
    return (
      <div className="sticky top-0 w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Live Preview</h2>
        </div>
        <div 
          {...getRootProps()}
          className={`aspect-[1/1.4] rounded-xl border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center bg-white
            ${isDragActive ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:border-emerald-200'}`}
        >
          <input {...getInputProps()} />
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl" role="img" aria-label="magic wand">✨</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Tailor your CV</h3>
            <p className="text-sm text-gray-600 mb-2">
              {isDragActive ? 'Drop your CV here' : 'Drag and drop your CV here, or'}
            </p>
            {!isDragActive && (
              <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                Choose file
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 w-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">Live Preview</h2>
        <button className="text-sm text-emerald-600 hover:text-emerald-700">
          Expand view
        </button>
      </div>
      <div className="aspect-[1/1.4] rounded-xl overflow-hidden bg-white shadow-lg h-[640px]">
        <iframe
          src={previewUrl}
          className="w-full h-full"
          title="CV Preview"
        />
      </div>
    </div>
  );
}